import { useLingui } from '@lingui/react/macro'
import {
  Badge,
  Callout,
  Hint,
  List,
  Panel,
  PanelSubtitle,
  PanelTitle,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import type { SentinelEvaluation } from 'features/sentinelDesign/types'
import { METHOD_LABELS } from 'features/timeSeries/experiment/knobCopy'
import { FC } from 'react'
import styled from 'styled-components'

const Assessment = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.55;
  white-space: pre-wrap;
`

/**
 * How a confidence reads: a [0, 1] number becomes a percentage, a label passes
 * through, and anything else is treated as unstated.
 */
const confidenceLabel = (
  confidence: number | string | null | undefined
): string | null => {
  if (typeof confidence === 'number' && Number.isFinite(confidence)) {
    return confidence <= 1
      ? `${Math.round(confidence * 100)}% confident`
      : String(confidence)
  }
  if (typeof confidence === 'string' && confidence.trim()) {
    return confidence.trim()
  }
  return null
}

/**
 * Coerce a value the evaluator may return as a string OR a structured object
 * to renderable text — the contract does not pin these shapes, and a raw object
 * child crashes React. Per-method verdicts in particular come back as
 * `{method, verdict, assessment}` from some evaluate runs; pull the readable
 * fields out rather than throwing.
 */
const toText = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    const parts = [o.verdict, o.assessment, o.summary, o.note].filter(
      (p): p is string => typeof p === 'string' && p.trim() !== ''
    )
    if (parts.length > 0) return parts.join(' — ')
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export interface EvaluationPanelProps {
  evaluation: SentinelEvaluation
}

/**
 * The evaluator's verdict on the fitted run: the overall read, the per-method
 * read, what might explain the deviations it saw (with its evidence and
 * confidence), and what to go check.
 */
export const EvaluationPanel: FC<EvaluationPanelProps> = ({ evaluation }) => {
  const { i18n } = useLingui()
  // The contract does not pin the shape of these three, so a scalar or a
  // missing field renders as "not reported" instead of throwing.
  const perMethod = Object.entries(evaluation.per_method ?? {})
  const causes = Array.isArray(evaluation.suspected_causes)
    ? evaluation.suspected_causes
    : []
  const checks = Array.isArray(evaluation.recommended_checks)
    ? evaluation.recommended_checks
    : []

  return (
    <Panel aria-label='Model evaluation'>
      <Row>
        <PanelTitle>Evaluation</PanelTitle>
        {causes.length > 0 && <Badge>{causes.length} suspected causes</Badge>}
        {checks.length > 0 && <Badge>{checks.length} recommended checks</Badge>}
      </Row>

      <Assessment>{toText(evaluation.assessment)}</Assessment>

      {perMethod.length > 0 && (
        <>
          <PanelSubtitle>Per method</PanelSubtitle>
          <StatTable>
            <tbody>
              {perMethod.map(([key, verdict]) => (
                <tr key={key}>
                  <th scope='row'>
                    {METHOD_LABELS[key] ? i18n._(METHOD_LABELS[key]) : key}
                  </th>
                  <td>{toText(verdict)}</td>
                </tr>
              ))}
            </tbody>
          </StatTable>
        </>
      )}

      {causes.length > 0 && (
        <>
          <PanelSubtitle>Suspected causes</PanelSubtitle>
          {causes.map((cause, index) => {
            const confidence = confidenceLabel(cause.confidence)
            return (
              <Callout key={index}>
                <Row>
                  <strong>{toText(cause.cause)}</strong>
                  {confidence && <Badge>{confidence}</Badge>}
                </Row>
                {cause.evidence && <Hint>{toText(cause.evidence)}</Hint>}
              </Callout>
            )
          })}
        </>
      )}

      {checks.length > 0 && (
        <>
          <PanelSubtitle>Recommended checks</PanelSubtitle>
          <List>
            {checks.map((check, index) => (
              <li key={index}>{toText(check)}</li>
            ))}
          </List>
        </>
      )}

      {perMethod.length === 0 && causes.length === 0 && checks.length === 0 && (
        <Hint>
          The evaluator returned an overall read only — no per-method verdict,
          suspected cause, or recommended check.
        </Hint>
      )}
    </Panel>
  )
}
